// @ts-strict-ignore
import type {
  SdkConfigPayload,
  SaveConfig,
  Props,
  SdkConfigFormState,
  Condition,
} from './types';
import { REQUIRED_SIGNALS_BY_DOCUMENT_TYPE } from './Plugins/IntellicheckIDVerification/constants';

export const docOptionsMap = {
  license: 'License',
  passport: 'Passport',
  canada_provincial_id: 'Canada Provincial ID',
  indigenous_card: 'Canada Indigenous Card',
  paystub: 'Paystub',
  bank_statement: 'Bank Statement',
  w2: 'W-2',
  1099: '1099',
  1120: '1120',
  1065: '1065',
  t1: 'T1 (Canadian tax doc)',
  t4: 'T4 (Canadian tax doc)',
  document: 'Document',
};

const docLabelMap = {
  1065: '1065',
  1099: '1099',
  1120: '1120',
  bank_statement: 'Bank Statement',
  brokerage_statement: 'Brokerage Statement',
  document: 'Document',
  license: 'License',
  passport: 'Passport',
  paystub: 'Paystub',
  t1: 'T1 (Canadian tax doc)',
  t4: 'T4 (Canadian tax doc)',
  utility: 'Utility',
  w2: 'W-2',
};

const STYLE_CUSTOMIZATION_SLUGS = [
  'prove_v2_style_customization',
  'informational_style_customization',
  'idcheckio_style_customization',
  'alloy_otp_style_customization',
  'trinsic_eid_style_customization',
];

export const proveOptionalFields = [
  'prove_v2_consent_language',
  'prove_v2_check_mobile_device_consent_language',
  'prove_v2_check_mobile_device_request_new_link',
  'prove_v2_link_to_terms',
  'prove_v2_check_number_link_terms',
  'prove_v2_check_number_consent_text',
  'prove_v2_analytics_tool',
  'prove_v2_analytics_script_url',
  'prove_v2_analytics_landing_page_view_event',
  'prove_v2_landing_page_continue_button_click_event',
  'prove_v2_check_your_mobile_device_page_view_event',
  'prove_v2_edit_your_mobile_number_link_click_event',
  'prove_v2_request_a_new_link_link_click_event',
  'prove_v2_invalid_phone_number_page_view_event',
  'prove_v2_invalid_phone_number_review_button_click_event',
  'prove_v2_check_your_mobile_number_page_view_event',
  'prove_v2_check_your_mobile_number_continue_button_click_event',
  'prove_v2_link_expired_page_view_event',
  'prove_v2_link_expired_request_new_link_button_click_event',
];

export const getDefaultValues = (
  sdkConfig: SdkConfigPayload,
  blockedPlugins: string[],
): SdkConfigFormState => {
  const defaultPlugin = blockedPlugins.includes(sdkConfig.plugin)
    ? null
    : sdkConfig.plugin;

  return sdkConfig.fields.reduce(
    (formObj: SdkConfigFormState, field) => {
      if (!formObj.fields[field.plugin]) {
        formObj.fields[field.plugin] = {};
      }

      // @ts-ignore TODO: have another ticket to fix config types
      if (field.slug === 'dynamic_documents' && !!field.value?.length) {
        // @ts-ignore TODO: same as above
        field.value.reduce(
          (acc, rule, i) => {
            const totalDocumentCount = {};
            const category = {};
            let tab = '';

            const ruleDocs = rule.documents.map(doc => {
              totalDocumentCount[doc.document_name] = doc.quantity;
              category[doc.document_name] = {
                value: doc.category,
                label: docLabelMap[doc.category],
              };
              if (rule.rule_type === 'Outcome reason') {
                acc.outcomeReasonDocumentHelpText[`reason${i}`] = {
                  ...acc.outcomeReasonDocumentHelpText[`reason${i}`],
                  [doc.document_name]: {
                    helpText: doc.help_text,
                  },
                };
                tab = 'conditional';
              }
              if (rule.rule_type === 'Static') {
                acc.alwaysRequiredDocumentHelpText[
                  doc.document_name
                ] = { helpText: doc.help_text };
                tab = 'always';
              }
              return {
                // TODO: spread this in branch wiring up outcome reasons
                icon: '',
                label: doc.document_name,
                value: doc.document_name.toLowerCase(),
              };
            });
            acc.conditions.push({
              tab,
              requiredDocuments: ruleDocs,
              outcomeReason: { label: rule.rule_name, value: `reason${i}` },
              totalDocumentCount,
              category,
            });
            formObj.fields.MultiDoc = {
              alwaysRequiredDocumentHelpText:
                acc.alwaysRequiredDocumentHelpText || null,
              outcomeReasonDocumentHelpText:
                acc.outcomeReasonDocumentHelpText || null,
              conditions: acc.conditions || [],
            };
            return acc;
          },
          {
            conditions: [],
            outcomeReasonDocumentHelpText: {},
            alwaysRequiredDocumentHelpText: {},
          },
        );
      } else if (STYLE_CUSTOMIZATION_SLUGS.includes(field.slug) && ((typeof field.value === 'string' && !!field.value) || (typeof field.defaultValue === 'string' && !!field.defaultValue))) {
        let styleObject: { [key: string]: string } = {};
        if (field.value && typeof field.value === 'string') {
          try {
            const parsed = JSON.parse(field.value);
            if (typeof parsed === 'object' && !Array.isArray(parsed)) {
              styleObject = parsed;
            }
          } catch {
            return formObj;
          }
        } else if (field.defaultValue && typeof field.defaultValue === 'string') {
          try {
            const parsed = JSON.parse(field.defaultValue);
            if (typeof parsed === 'object' && !Array.isArray(parsed)) {
              styleObject = parsed;
            }
          } catch {
            return formObj;
          }
        }
        Object.keys(styleObject).forEach(key => {
          formObj.fields[field.plugin][key] = styleObject[key];
        });
      } else {
        switch (field.dataType) {
          case 'checkbox':
            formObj.fields[field.plugin][field.slug] = field.value ?? field.defaultValue;
            break;
          case 'text':
          case 'text_paragraph':
          case 'dropdown':
          case 'number':
          case 'radio_button':
            formObj.fields[field.plugin][field.slug] = field.value || field.defaultValue || '';
            break;
          case 'multiselect':
            formObj.fields[field.plugin][field.slug] = field.value || field.defaultValue || [];
            break;
          case 'header':
            formObj.fields[field.plugin][field.slug] = field.value || field.displayName;
            break;
          case 'footer_url':
            formObj.fields[field.plugin][field.slug] = field.value || field.displayName;
            break;
        }
      }

      return formObj;
    },
    { selectedPlugin: defaultPlugin, fields: {} },
  );
};

export const jsonFormatStyles = styles => JSON.stringify(styles);

export const formatDocuments = multiDocFields => multiDocFields?.conditions?.reduce((acc, cond) => {
  const rule = { rule_name: '', rule_type: '', documents: [] };
  rule.rule_name = cond.outcomeReason?.label || '';
  if (cond.tab === 'conditional') {
    rule.rule_type = 'Outcome reason';
  } else {
    rule.rule_type = 'Static';
  }
  cond.requiredDocuments.map(doc => {
    const currDoc = {
      document_name: '',
      quantity: 0,
      help_text: '',
      category: '',
    };
    currDoc.document_name = doc.label;
    currDoc.quantity = cond.totalDocumentCount
      ? cond.totalDocumentCount[doc.label]
      : null;
    currDoc.help_text = cond.tab === 'conditional'
      ? multiDocFields?.outcomeReasonDocumentHelpText?.[cond.outcomeReason?.value]?.[doc.label]?.helpText ?? ''
      : multiDocFields?.alwaysRequiredDocumentHelpText?.[doc.label]?.helpText ?? '';
    currDoc.category = cond.category[doc.label].value;
    return rule.documents.push(currDoc);
  });
  acc.dynamicDocuments.push(rule);
  return acc;
}, { dynamicDocuments: [] });

// returns true if content tab is valid
export const contentTabValidation = (multiDocValues: SdkConfigFormState['fields']['MultiDoc']) => {
  // returns true when invalid
  const validateParentLevelFields = Object.keys(multiDocValues).some(field => {
    const value = multiDocValues[field];
    if (typeof value === 'string' && !value.length && field !== 'mobile_flow_prompt_text') {
      return true;
    }
    return false;
  });

  return !validateParentLevelFields;
};

// returns true if content tab is valid
export const informationalValidation = (formValues: SdkConfigFormState['fields']['Informational']) => {
  // returns true when invalid
  const hasIncompleteFields = Object.keys(formValues).some(field => {
    const value = formValues[field];
    // Hardcoding fields to skip our check (these are not required fields)
    // TODO: Investigate why react hook form is not returning the proper error messages.
    if (field.includes('informational_style') || field === 'informational_sub_header_text') {
      return false;
    }
    if (typeof value === 'string' && !value.length) {
      return true;
    }
    return false;
  });
  return !hasIncompleteFields;
};

export const trinsicElectronicIdValidation = (formValues: SdkConfigFormState['fields']['TrinsicElectronicID']) => {
  const hasIncompleteFields = Object.keys(formValues).some(field => {
    const value = formValues[field];
    if (field.includes('trinsic_eid_style') || field === 'trinsic_eid_include_caption') {
      return false;
    }
    if (field === 'trinsic_eid_caption_text') {
      if (formValues.trinsic_eid_include_caption && typeof value === 'string' && !value.length) {
        return true;
      }
      return false;
    }
    if (typeof value === 'string' && !value.length) {
      return true;
    }
    return false;
  });

  return !hasIncompleteFields;
};

const intellicheckOptionalFields = [
  'intellicheckidv_device_validation',
  'intellicheckidv_purpose',
  'intellicheckidv_return_images',
];

export const intellicheckIdVerificationValidation = (
  formValues: SdkConfigFormState['fields']['IntellicheckIDVerification'],
) => {
  const hasIncompleteFields = Object.keys(formValues).some(field => {
    const value = formValues[field];
    if (intellicheckOptionalFields.includes(field)) {
      return false;
    }
    if (typeof value === 'string' && !value.length) {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  });

  // Check that the required signal for the document type is present
  const documentType = typeof formValues.intellicheckidv_document_type === 'string'
    ? formValues.intellicheckidv_document_type
    : undefined;
  const signals = Array.isArray(formValues.intellicheckidv_signals)
    ? formValues.intellicheckidv_signals
    : [];
  const requiredSignal = REQUIRED_SIGNALS_BY_DOCUMENT_TYPE[documentType];
  const hasRequiredSignal = !requiredSignal || signals.includes(requiredSignal);

  return !hasIncompleteFields && hasRequiredSignal;
};

const otpFieldsToSkip = [
  'alloy_otp_twilio_service_friendly_name',
  'alloy_otp_twilio_service_sid',
  'alloy_otp_twilio_template_id',
  'alloy_otp_analytics_tool',
  'alloy_otp_analytics_script_url',
  'alloy_otp_method_selection_page_view_event',
  'alloy_otp_sms_method_selection_click_event',
  'alloy_otp_email_method_selection_click_event',
  'alloy_otp_voice_call_method_selection_click_event',
  'alloy_otp_method_selection_continue_button_click_event',
  'alloy_otp_enter_code_page_view_event',
  'alloy_otp_one_time_code_submit_click_event',
  'alloy_otp_resend_code_link_click_event',
  'alloy_otp_general_error_page_view_event',
  'alloy_otp_reach_out_link_click_event',
  'alloy_otp_general_error_continue_button_click_event',
  'alloy_otp_invalid_phone_number_page_view_event',
  'alloy_otp_invalid_phone_number_try_again_button_click_event',
  'alloy_otp_code_expired_page_view_event',
  'alloy_otp_code_expired_try_again_button_click_event',
];

export const otpValidation = (formValues: SdkConfigFormState['fields']['AlloyOTP']) => {
  // returns true when invalid
  const hasIncompleteFields = Object.keys(formValues).some(field => {
    const value = formValues[field];
    // Hardcoding fields to skip our check (these are not required fields)
    // TODO: Investigate why react hook form is not returning the proper error messages.
    if (field.includes('alloy_otp_style')
      || (field === 'alloy_otp_twilio_override_country_code_default_language' && value === false)
      || (field === 'alloy_otp_enter_code_page_expiration_countdown_text' && !formValues.alloy_otp_enter_code_page_include_expiration_countdown)
      || (field === 'alloy_otp_enter_code_page_include_expiration_countdown' && value === false)
      || (field === 'alloy_otp_twilio_template_id' && !formValues.alloy_otp_twilio_use_custom_message_template)
      || (field === 'alloy_otp_twilio_use_custom_message_template' && value === false)
      || (field === 'alloy_otp_delivery_method' && Array.isArray(value) && value.length > 1)
      || (field === 'alloy_otp_language' && !formValues.alloy_otp_twilio_override_country_code_default_language)
      || (field === 'alloy_otp_language_override' && value === false)
      || (field === 'alloy_otp_method_selection_page_skip_if_mobile_number_provided')
      || otpFieldsToSkip.includes(field)
    ) {
      return false;
    }
    if (typeof value === 'string' && !value.length) {
      return true;
    }
    return false;
  });
  return !hasIncompleteFields;
};

export const proveContentTabValidation = (formValues: SdkConfigFormState['fields']['ProveV2']) => {
  // returns true when invalid
  const validateParentLevelFields = Object.keys(formValues).some(field => {
    const value = formValues[field];
    // Hardcoding fields to skip our check (these are not required fields)
    // TODO: Investigate why react hook form is not returning the proper error messages.
    if (
      field.includes('prove_v2_style')
      || proveOptionalFields.includes(field)
    ) {
      return false;
    }
    if (typeof value === 'string' && !value.length) {
      return true;
    }
    return false;
  });

  return !validateParentLevelFields;
};

// returns true when logic tab is valid
export const logicTabValidation = (multiDocValues: SdkConfigFormState['fields']['MultiDoc']) => {
  const conditions = multiDocValues.conditions as Condition[] || [];
  const isInvalidRequiredDocuments = conditions.some(({
    requiredDocuments, totalDocumentCount = {},
  }) => (
    !requiredDocuments.length || Object.keys(totalDocumentCount)?.some(
      key => totalDocumentCount[key] > 6 || totalDocumentCount[key] < 0,
    )),
  );
  return !isInvalidRequiredDocuments;
};

const PLUGIN_TO_STYLE_PREFIX_MAP = {
  ProveV2: 'prove_v2_styles',
  Informational: 'informational_styles',
  IDCheckIo: 'idcheckio_styles',
  AlloyOTP: 'alloy_otp_styles',
  TrinsicElectronicID: 'trinsic_eid_styles',
};

export const getOnSubmit = (
  oldConfig: SdkConfigPayload,
  saveConfig: SaveConfig,
  onClose: Props['onClose'],
  clearErrors: any,
  setError: any,
) => (formState: SdkConfigFormState) => {
  const newConfig: SdkConfigPayload = {
    plugin: formState.selectedPlugin,
    fields: [],
  };

  const values = formState?.fields?.MultiDoc;
  if ((!contentTabValidation(values) || !logicTabValidation(values))
    && newConfig.plugin === 'MultiDoc'
  ) {
    setError();
    return;
  }

  try {
    newConfig.fields = oldConfig.fields.map(field => {
      const newField = { ...field };
      // @ts-ignore TODO: Clean up types - used to be 1:1 but more complicated now with dynamic values
      newField.value = formState.fields[field.plugin][field.slug];
      if (field.plugin === 'MultiDoc' && newConfig.plugin === 'MultiDoc') {
        const { dynamicDocuments } = formatDocuments(formState.fields.MultiDoc);
        if (field.slug === 'dynamic_documents' && dynamicDocuments.length) {
          newField.value = dynamicDocuments;
        }
      }

      if ((field.plugin === 'ProveV2' && newConfig.plugin === 'ProveV2' && field.slug === 'prove_v2_style_customization')
        || (field.plugin === 'Informational' && newConfig.plugin === 'Informational' && field.slug === 'informational_style_customization')
        || (field.plugin === 'IDCheckIo' && newConfig.plugin === 'IDCheckIo' && field.slug === 'idcheckio_style_customization')
        || (field.plugin === 'AlloyOTP' && newConfig.plugin === 'AlloyOTP' && field.slug === 'alloy_otp_style_customization')
        || (field.plugin === 'TrinsicElectronicID' && newConfig.plugin === 'TrinsicElectronicID' && field.slug === 'trinsic_eid_style_customization')
      ) {
        const styles = {};
        const pluginName = field.plugin;
        const check = PLUGIN_TO_STYLE_PREFIX_MAP[pluginName];

        Object.keys(formState.fields[pluginName]).forEach(key => {
          if (key.includes(check)) {
            styles[key] = formState.fields[pluginName][key];
          }
        });
        newField.value = jsonFormatStyles(styles);
      }
      return newField;
    });
    clearErrors();
    saveConfig(newConfig);
    onClose();
  } catch (err) {
    setError();
  }
};

export const getPluginName = (selectedPlugin): string => {
  switch (selectedPlugin) {
    case 'DocV': {
      return 'Alloy Single Document Collection';
    }
    case 'MultiDoc': {
      return 'Alloy Multi-Document Collection';
    }
    case 'ClearVerified': {
      return 'CLEAR';
    }
    case 'AlloyOTP': {
      return 'Alloy OTP';
    }
    case 'ProveMobileAuth': {
      return 'Prove Mobile Auth';
    }
    case 'ProveV2': {
      return 'Prove Instant Auth';
    }
    case 'TrinsicElectronicID': {
      return 'Trinsic eID';
    }
    case 'IDNowVideoIdent': {
      return 'IDNow VideoIdent';
    }
    case 'IntellicheckIDVerification': {
      return 'Intellicheck ID Verification';
    }
    default: {
      return selectedPlugin;
    }
  }
};

export const getPluginOptions = (
  fields: SdkConfigPayload['fields'],
  blockedPlugins: string[],
) => fields.reduce(
  (plugins, field) => {
    if (
      plugins.find(plugin => plugin.value === field.plugin)
      || blockedPlugins.includes(field.plugin)
    ) {
      return plugins;
    }

    const pluginObj = {
      value: field.plugin,
      label: getPluginName(field.plugin),
    };
    return plugins.concat(pluginObj);
  },
  [{ label: 'None', value: '' }],
);

export const getDropdownOptions = (pValues: string[]) => pValues.reduce((options, pValue) => {
  const optionObj = {
    value: pValue,
    label: pValue,
  };
  return options.concat(optionObj);
}, []);

/**
 * Special method to show the pretty DocV display names
 */
export const getMultiselectLabel = (fieldOption: string, plugin: string) => {
  if (plugin !== 'DocV') {
    return fieldOption;
  }
  return docOptionsMap[fieldOption] || fieldOption;
};

export const transformSdkConfigForSave = sdkConfig => sdkConfig.fields.reduce(
  (acc, field) => {
    acc[field.slug] = field.value;
    return acc;
  },
  { plugin: sdkConfig.plugin },
);

export const biometricDocuments = [
  'license',
  'passport',
  'canada_provincial_id',
  'indigenous_card',
];

export const getDocTypeOptions = () => Object.keys(docLabelMap).map(doc => ({
  value: doc,
  label: docLabelMap[doc],
}));

export const getDocumentOptions = (savedDocuments, defaultDocuments) => {
  const docMap = {};
  defaultDocuments.forEach(defaultDoc => docMap[defaultDoc.label]);
  return [
    ...defaultDocuments,
    ...savedDocuments.filter(saved => !docMap[saved.label]),
  ];
};
